import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Camera, Trash, Check, MapPin, GenderMale, GenderFemale } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import api, { API } from '../lib/api';
import { SILHOUETTE_AVATAR } from '../lib/avatar';
import { WORLD_COUNTRIES } from '../lib/countries';

const COUNTRIES_MAP = {
  slovenia: 'Slovenia', croatia: 'Croatia', bosnia: 'Bosnia & Herzegovina',
  serbia: 'Serbia', montenegro: 'Montenegro', north_macedonia: 'North Macedonia', kosovo: 'Kosovo'
};

export default function ProfileEditPage() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [currentCity, setCurrentCity] = useState(user?.current_city || '');
  const [currentCountry, setCurrentCountry] = useState(user?.current_country || '');
  const [heritage, setHeritage] = useState(user?.heritage_country || '');
  const [gender, setGender] = useState(user?.gender || '');
  const [lookingFor, setLookingFor] = useState(user?.looking_for || '');
  const [ageRange, setAgeRange] = useState([user?.age_range_min || 18, user?.age_range_max || 45]);
  const [photos, setPhotos] = useState(user?.photos || []);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saved, setSaved] = useState(false);

  if (!user) { navigate('/auth'); return null; }

  const getPhotoUrl = (photo) => {
    if (photo?.url) return photo.url;
    if (photo?.storage_path) return `${API}/files/${photo.storage_path}`;
    return SILHOUETTE_AVATAR;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/users/me', {
        first_name: firstName,
        last_name: lastName,
        bio,
        current_city: currentCity,
        current_country: currentCountry,
        heritage_country: heritage,
        gender,
        looking_for: lookingFor,
        age_range_min: ageRange[0],
        age_range_max: ageRange[1]
      });
      updateUser(data.user);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Save failed', err);
    }
    setSaving(false);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/photos/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const updated = [...photos, data.photo];
      setPhotos(updated);
      updateUser({ photos: updated });
    } catch {}
    setUploading(false);
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await api.delete(`/photos/${photoId}`);
      const updated = photos.filter(p => p.id !== photoId);
      setPhotos(updated);
      updateUser({ photos: updated });
    } catch {}
  };

  return (
    <div className="min-h-screen pt-20 pb-8 px-4" data-testid="profile-edit-page">
      <div className="max-w-lg mx-auto">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#9EABCE] hover:text-white mb-6 transition-colors" data-testid="edit-back-btn">
          <ArrowLeft size={20} /> Back
        </button>

        <h1 className="text-2xl md:text-3xl font-medium tracking-tight text-white font-['Outfit'] mb-8">Edit Profile</h1>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
          {/* Photos Section */}
          <div className="glass-card p-6" data-testid="photos-section">
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Photos ({photos.length}/50)</p>
              {photos.length === 0 && (
                <span className="text-[10px] font-bold uppercase tracking-wider text-red-400">Required</span>
              )}
            </div>
            {photos.length === 0 && (
              <div className="mb-4 p-4 rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/5 flex items-start gap-3" data-testid="no-photos-prompt">
                <Camera size={20} weight="light" className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white text-sm font-medium">Add your first photo</p>
                  <p className="text-[#9EABCE] text-xs mt-0.5">Profiles without photos won't appear in Browse. Upload at least one to be seen by others.</p>
                </div>
              </div>
            )}
            <div className="grid grid-cols-4 gap-3">
              {photos.map((photo) => (
                <div key={photo.id} className="relative aspect-square rounded-xl overflow-hidden group">
                  <img src={getPhotoUrl(photo)} alt="" className="w-full h-full object-cover" />
                  <button
                    onClick={() => handleDeletePhoto(photo.id)}
                    className="absolute top-1 right-1 p-1.5 rounded-lg bg-black/60 text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`delete-photo-${photo.id}`}
                  >
                    <Trash size={14} />
                  </button>
                </div>
              ))}
              {photos.length < 50 && (
                <label className="aspect-square rounded-xl border-2 border-dashed border-[#D4AF37]/20 flex items-center justify-center cursor-pointer hover:border-[#D4AF37]/40 hover:bg-[#D4AF37]/5 transition-all" data-testid="add-photo-btn">
                  <Camera size={24} weight="light" className="text-[#D4AF37]/50" />
                  <input type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
                </label>
              )}
            </div>
            {uploading && <p className="text-xs text-[#D4AF37] mt-2">Uploading...</p>}
          </div>

          {/* Personal Info */}
          <div className="glass-card p-6 space-y-4">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Personal Info</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#9EABCE] text-xs mb-1 block">First Name</Label>
                <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="edit-firstname" className="bg-white/5 border-white/10 text-white rounded-xl" />
              </div>
              <div>
                <Label className="text-[#9EABCE] text-xs mb-1 block">Last Name</Label>
                <Input value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid="edit-lastname" className="bg-white/5 border-white/10 text-white rounded-xl" />
              </div>
            </div>
            <div>
              <Label className="text-[#9EABCE] text-xs mb-1 block">About You</Label>
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                rows={3}
                data-testid="edit-bio"
                className="w-full bg-white/5 border border-white/10 text-white rounded-xl p-3 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#D4AF37] focus:border-[#D4AF37]"
              />
            </div>
            <div>
              <Label className="text-[#9EABCE] text-xs mb-1 block flex items-center gap-1"><MapPin size={12} /> Current City</Label>
              <Input value={currentCity} onChange={(e) => setCurrentCity(e.target.value)} data-testid="edit-city" className="bg-white/5 border-white/10 text-white rounded-xl" />
            </div>
            <div>
              <Label className="text-[#9EABCE] text-xs mb-1 block flex items-center gap-1"><MapPin size={12} /> Current Country</Label>
              <Select value={currentCountry} onValueChange={setCurrentCountry}>
                <SelectTrigger data-testid="edit-current-country" className="bg-white/5 border-white/10 text-white rounded-xl">
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent className="bg-[#0B112B] border-white/10 max-h-[280px]">
                  {WORLD_COUNTRIES.map(c => (
                    <SelectItem key={c.code} value={c.code} className="text-white">{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Heritage & Preferences */}
          <div className="glass-card p-6 space-y-4">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Preferences</p>
            <div>
              <Label className="text-[#9EABCE] text-xs mb-1 block">Heritage</Label>
              <Select value={heritage} onValueChange={setHeritage}>
                <SelectTrigger data-testid="edit-heritage" className="bg-white/5 border-white/10 text-white rounded-xl">
                  <SelectValue placeholder="Select heritage" />
                </SelectTrigger>
                <SelectContent className="bg-[#0B112B] border-white/10">
                  {Object.entries(COUNTRIES_MAP).map(([key, name]) => (
                    <SelectItem key={key} value={key} className="text-white">{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#9EABCE] text-xs mb-1 block flex items-center gap-1">I am a</Label>
                <Select value={gender} onValueChange={setGender}>
                  <SelectTrigger data-testid="edit-gender" className="bg-white/5 border-white/10 text-white rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0B112B] border-white/10">
                    <SelectItem value="man" className="text-white"><span className="flex items-center gap-2"><GenderMale size={14} /> Man</span></SelectItem>
                    <SelectItem value="woman" className="text-white"><span className="flex items-center gap-2"><GenderFemale size={14} /> Woman</span></SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#9EABCE] text-xs mb-1 block">Looking for</Label>
                <Select value={lookingFor} onValueChange={setLookingFor}>
                  <SelectTrigger data-testid="edit-looking-for" className="bg-white/5 border-white/10 text-white rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0B112B] border-white/10">
                    <SelectItem value="man" className="text-white"><span className="flex items-center gap-2"><GenderMale size={14} /> Man</span></SelectItem>
                    <SelectItem value="woman" className="text-white"><span className="flex items-center gap-2"><GenderFemale size={14} /> Woman</span></SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-[#9EABCE] text-xs mb-1 block">Age Range: {ageRange[0]} - {ageRange[1]}</Label>
              <Slider
                value={ageRange}
                onValueChange={setAgeRange}
                min={18}
                max={65}
                step={1}
                data-testid="edit-age-range"
                className="mt-3 [&_[role=slider]]:bg-[#D4AF37] [&_[role=slider]]:border-[#D4AF37] [&_[data-orientation=horizontal]>.relative]:bg-[#D4AF37]"
              />
            </div>
          </div>

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="edit-save-btn"
            className={`w-full py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all duration-300 ${
              saved ? 'bg-green-500/20 text-green-400 border border-green-500/20' : 'btn-gold'
            } disabled:opacity-50`}
          >
            {saving ? 'Saving...' : saved ? (<><Check size={18} weight="bold" /> Saved!</>) : 'Save Changes'}
          </button>
        </motion.div>
      </div>
    </div>
  );
}
